/**
 * Loads a file as an ArrayBuffer from a specified path.
 *
 * This version of `loadBuffer` uses only browser APIs and is suitable for use
 * in the browser.
 *
 * @param prefix - An array of strings representing the path segments leading to
 * the file.
 * @param filename - The name of the file to load.
 * @returns A promise that resolves to the file's content as an ArrayBuffer.
 * @throws Will throw an error if the file cannot be loaded.
 */
export async function loadBuffer(
  prefix: string[],
  filename: string
): Promise<ArrayBuffer> {
  const path = pathJoiner(prefix.concat(filename));

  return fetch(path).then(response => {
    if (!response.ok) {
      throw new Error(`Failed to load file at ${path}: ${response.statusText}`);
    }
    return response.arrayBuffer();
  });
}

/**
 * Joins an array of string elements into a single path string, separated by '/'.
 *
 * This version of `pathJoiner` uses only browser APIs and is suitable for use
 * in the browser.
 *
 * @param elements - An array of strings representing parts of the path.
 * @returns A single string with all elements joined by '/'.
 */
export function pathJoiner(elements: string[]) {
  // Build an absolute URL rooted at Vite's base (usually "/").
  // This prevents requests like "canvaskit.wasm" from resolving relative
  // to hashed chunk URLs under "/assets/" in production.
  const base = import.meta.env.BASE_URL || '/';
  const path = elements.filter(Boolean).join('/');
  return (base.endsWith('/') ? base : base + '/') + path.replace(/^\/+/, '');
}
